// BILLING-001 Plans & pricing (owner, 2026-10-25; D-065). The one catalog of everything a plan can
// switch on, limit or give: every vendor-admin feature (a flag), every limit (a quota) and the
// monthly credits (SMS, root AI). The owner console's plan editor lists it, the server gates routes
// with it, the vendor admin locks sidebar items with it and shows the icon of the plan that unlocks
// them. Keys are `plan_entitlements.key`; the older keys keep their names (migration 0103).
import type { WalletActivity } from "./wallet-service";
import type { SetupStep, ShopHealth } from "./console-desk";

/** Feature groups, in the order the plan editor and the comparison table list them. */
export const PLAN_FEATURE_GROUPS = [
  "selling",
  "channels",
  "catalog",
  "customers",
  "marketing",
  "money",
  "store",
  "ai",
  "team",
  "developers",
  "services",
  "limits",
  "credits",
] as const;
export type PlanFeatureGroup = (typeof PLAN_FEATURE_GROUPS)[number];

export type PlanFeatureKind = "flag" | "quota" | "credit";

export interface PlanFeatureDef {
  key: string;
  group: PlanFeatureGroup;
  kind: PlanFeatureKind;
  /** Vendor-admin nav item IDs (apps/admin shell/nav.ts) this feature unlocks. */
  nav?: readonly string[];
  /** Vendor route-ID prefixes the server refuses while the feature is off (writes only unless `block: "all"`). */
  routes?: readonly string[];
  block?: "writes" | "all";
  /** Value for a new plan and for plans made before the key existed (flags 0/1; quotas -1 = unlimited). */
  initial: number;
  /** How a quota is counted (for display). */
  unit?: "count" | "bytes" | "days" | "per_min" | "month";
}

const f = (
  key: string,
  group: PlanFeatureGroup,
  nav?: readonly string[],
  routes?: readonly string[],
  block?: "writes" | "all",
): PlanFeatureDef => ({
  key,
  group,
  kind: "flag",
  initial: 1,
  ...(nav ? { nav } : {}),
  ...(routes ? { routes } : {}),
  ...(block ? { block } : {}),
});
const q = (
  key: string,
  initial: number,
  unit: PlanFeatureDef["unit"] = "count",
): PlanFeatureDef => ({
  key,
  group: "limits",
  kind: "quota",
  initial,
  unit,
});

/**
 * Every feature, limit and credit. Flags added by D-065 start ON for the plans that existed before
 * (nothing a shop uses today disappears); the older flags keep their seeded values (Free: off).
 */
export const PLAN_FEATURES: readonly PlanFeatureDef[] = [
  // Core pages (owner, 2026-10-27: "plan control every feature, not only add-ons"). On in every plan
  // until the owner turns one off. Home, Wallet, Billing, Organization, Team, Store profile, Language
  // and region and Help are never locked: a shop always needs them to run its account and upgrade.
  // Unfinished checkouts have their own switch (below); the route stays here too, so a plan without
  // Orders can't use them either.
  f("order_management", "selling", ["orders"], ["orders.", "checkout.unfinished", "invoices."]),
  f("returns", "selling", ["returns"], ["returns."]),
  f(
    "delivery",
    "selling",
    ["couriers", "delivery"],
    [
      "shipping.couriers.",
      "shipping.zones.",
      "shipping.profiles.",
      "shipping.shipments.",
      "shipping.installation.",
    ],
  ),
  f(
    "checkout_settings",
    "selling",
    ["checkout"],
    ["checkout.settings.", "risk.rules.", "risk.lists."],
  ),
  f(
    "product_catalog",
    "catalog",
    ["products", "categories", "collections", "brands"],
    ["products.", "variants.", "categories.", "collections.", "brands.", "spec_presets."],
  ),
  f(
    "inventory",
    "catalog",
    ["inventory"],
    ["inventory.adjustments", "inventory.levels", "inventory.movements"],
  ),
  f("bulk_import", "catalog", ["import", "moveStore"], ["imports.", "migrations."]),
  f("media_library", "catalog", ["media"], ["media."]),
  f("customer_list", "customers", ["customers"], ["customers."]),
  f("payments", "money", ["payments", "paymentSettings"], ["payments."]),
  f(
    "money_overview",
    "money",
    ["money", "cod"],
    ["finance.cod.", "finance.settlements.", "finance.cash_book"],
  ),
  f("taxes", "money", ["taxes"], ["tax."]),
  f("marketing_automations", "marketing", ["automations"], ["automations."]),
  f(
    "store_notifications",
    "marketing",
    ["notificationSettings"],
    ["notify.templates.", "message_templates.", "sender_identities.", "mail_aliases."],
  ),
  f(
    "online_store",
    "store",
    ["storeHome", "templates", "theme", "menus", "content", "branches", "sellingPoints"],
    ["theme.", "content.list", "content.put", "content.delete", "blog."],
  ),
  f("seo_tools", "store", ["seo"], ["store.seo", "feeds."]),
  f("store_policies", "store", ["policies"], ["store.legal.", "content.policy_templates"]),
  f("reports", "store", ["reports"], ["analytics.", "reports."]),
  f("integrations", "developers", ["integrations"], ["integrations."]),
  // Selling and orders
  f("pos", "selling", ["pos", "sales", "registers"], ["pos."]),
  f("pos_offline", "selling"),
  f("cod_checks", "selling", ["codChecks"], ["risk.cod_verifications."]),
  f("multi_courier_routing", "selling", undefined, ["shipping.routing."]),
  // Plan audit (owner, 2026-11-05): features the platform has that the plan editor didn't list.
  f("abandoned_checkouts", "selling", ["unfinished"], ["checkout.unfinished"]),
  f("installation", "selling", ["installation"], ["shipping.installation."]),
  // Sales channels
  f("conversations", "channels", ["conversations", "inbox"], ["conversations.", "content.inbox."]),
  f("marketplace_sync", "channels", ["daraz"], ["daraz."]),
  f("customer_accounts", "channels"),
  // Products and stock
  f("advanced_inventory", "catalog", ["transfers"], ["inventory.transfers.", "inventory.batches."]),
  f("digital_products", "catalog", ["digitalProducts"], ["digital."]),
  f("bundles", "catalog"),
  // Customers
  f("segments", "customers", ["segments"], ["segments."]),
  f("reviews", "customers", ["reviews"], ["reviews."]),
  f("loyalty", "customers", ["loyalty"], ["loyalty."]),
  // Marketing
  f("promotions", "marketing", ["promotions"], ["coupons."]),
  // Plan audit (2026-11-05): the server now stops these too (before, only the sidebar locked).
  f("flash_sales", "marketing", ["flashDeals"], ["flash_deals."]),
  f("offer_popup", "marketing", ["offerPopup"], ["offer_popups."]),
  f("newsletter", "marketing", ["newsletter"], ["newsletter."]),
  f("payment_links", "marketing", ["links"], ["checkout.links."]),
  f("scheduled_campaigns", "marketing", ["messages"], ["campaigns.messages."]),
  f("affiliates", "marketing", ["affiliates"], ["affiliates.", "partners."]),
  f("dedicated_sender_id", "marketing"),
  // Sending one SMS to many customers from the SMS page (plan audit, 2026-11-05).
  f("bulk_sms", "marketing", undefined, ["sms.bulk."]),
  // Money
  f(
    "finance_advanced",
    "money",
    ["accounts", "statements"],
    ["finance.accounts.", "finance.journal."],
  ),
  f("expenses", "money", ["expenses", "suppliers"]),
  // Online store
  f("custom_domain", "store", ["domains"], ["store.domains."]),
  // Making and editing pages; publishing and the home page stay with the Online store (core).
  f("page_editor", "store", ["pageEditor"], ["theme.pages.create", "theme.pages.update"]),
  f("custom_code", "store", ["codeEditor"]),
  // Blog posts (a tab of Pages and blog): the pages themselves stay with the Online store.
  f("blog", "store", undefined, ["blog."]),
  f("remove_attribution", "store"),
  f("traffic_analytics", "store", ["traffic"]),
  f("advanced_analytics", "store"),
  // AI
  // The route IDs are "ai.chat" and "ai.generate" (no dot after them): the old "ai.chat." prefix never
  // matched, so root kept answering on plans with it off (plan audit, 2026-11-05).
  f("ai_assistant", "ai", undefined, ["ai.chat", "ai.generate", "ai.actions."], "all"),
  f("shopping_assistant", "ai"),
  // Approving an AI app (MCP sign-in, D-090); revoking stays open after a move down.
  f("mcp_access", "ai", ["mcp"], ["oauth.authorize"]),
  // Team: staff roles made by the shop (D-125); the preset roles stay on every plan.
  f("custom_roles", "team", undefined, ["team.roles.create", "team.roles.update"]),
  // Apps and developers
  // BILLING-001 audit (2026-10-27): the public API (API keys in use, `public.*`) needs the plan too,
  // reads included, so keys made on a higher plan or a trial stop working after a move down.
  f("public_api", "developers", ["apiKeys"], ["api_keys.", "public."], "all"),
  f("webhooks", "developers", ["webhooks"], ["webhooks.", "public.webhooks."]),
  f("headless_api", "developers", undefined, ["headless.keys."]),
  // Adyvance Services
  f("import_marketplace", "services", ["importMarketplace"], ["import."]),
  // The SMS page: buying credits, the automatic order SMS and bulk sends (plan audit, 2026-11-05).
  f(
    "sms_service",
    "services",
    ["smsService"],
    ["sms.purchases.", "sms.account.", "sms.events.", "sms.bulk."],
  ),
  f("digital_marketing", "services", ["digitalMarketing"], ["ads."]),
  f("design_content", "services", ["designContent"]),
  f("growth_strategy", "services", ["growthStrategy"]),
  f("courses", "services", ["courses"], ["courses."], "all"),
  // Limits
  q("stores", 1),
  q("products", 200),
  q("orders_month", 300, "month"),
  q("staff", 2),
  q("locations", 1),
  q("pos_registers", 0),
  q("storage_bytes", 2_147_483_648, "bytes"),
  q("automations", 0),
  q("api_keys", 0),
  q("webhook_endpoints", 0),
  q("api_rate_per_min", 120, "per_min"),
  q("theme_versions", 10),
  q("audit_retention_days", 730, "days"),
  q("test_mode_stores", 1),
  // Monthly credits: given every month, unused ones do not carry over (bought packs never expire).
  { key: "sms_month", group: "credits", kind: "credit", initial: 0, unit: "month" },
  { key: "ai_credits_month", group: "credits", kind: "credit", initial: 0, unit: "month" },
];

export const PLAN_FEATURE_BY_KEY: ReadonlyMap<string, PlanFeatureDef> = new Map(
  PLAN_FEATURES.map((d) => [d.key, d]),
);

/** `plan_entitlements.kind` for a catalog key (credits are monthly quotas in the resolver). */
export const entitlementKind = (key: string): "flag" | "quota" =>
  PLAN_FEATURE_BY_KEY.get(key)?.kind === "flag" ? "flag" : "quota";

/** The feature whose routes cover a vendor route ID, if any. */
export function featureForRoute(routeId: string): PlanFeatureDef | undefined {
  return featuresForRoute(routeId)[0];
}

/**
 * Every feature whose routes cover a vendor route ID (BILLING-001 audit, 2026-10-27): a route can sit
 * under a core feature and an add-on (theme.pages.create: Online store and Page editor), and the plan
 * gate needs all of them on.
 */
export function featuresForRoute(routeId: string): PlanFeatureDef[] {
  return PLAN_FEATURES.filter((d) => d.routes?.some((p) => routeId.startsWith(p)));
}

/** The feature that unlocks a vendor-admin nav item, if any. */
export function featureForNav(navId: string): PlanFeatureDef | undefined {
  return PLAN_FEATURES.find((d) => d.nav?.includes(navId));
}

/** Icons the owner can give a plan (names from @ady/ui Icon). */
export const PLAN_ICONS = [
  "leaf",
  // The plan crowns (owner, 2026-10-27): green for Startup, blue for Business.
  "crownStartup",
  "crownBusiness",
  "sparkle",
  "star",
  "zap",
  "rocket",
  "flame",
  "gem",
  "crown",
  "trophy",
  "shield",
  "target",
  "gift",
  "heart",
  "building",
  "store",
  "infinity",
] as const;
export type PlanIcon = (typeof PLAN_ICONS)[number];
export const isPlanIcon = (v: unknown): v is PlanIcon =>
  typeof v === "string" && (PLAN_ICONS as readonly string[]).includes(v);

/** The plan a locked feature is in: the cheapest plan on sale that turns it on. */
export interface PlanUnlock {
  plan_id: string;
  name: string;
  icon: PlanIcon;
  /** Cheapest monthly price, or the yearly price / 12, in paisa; null for a plan with no price. */
  monthly_paisa: number | null;
}

// ---------- Plans & pricing (owner console) ----------

export interface PlanEntitlementValue {
  key: string;
  kind: "flag" | "quota";
  value: number;
}

export interface PricingPlan {
  id: string;
  key: string;
  name: string;
  summary: string | null;
  badge: string | null;
  icon: PlanIcon;
  sort: number;
  /** On sale: shops can buy it. */
  active: boolean;
  /** The plan every shop is on until it buys one (the base plan; it has no price). */
  is_default: boolean;
  trial_days: number | null;
  prices: { monthly?: number; yearly?: number };
  entitlements: PlanEntitlementValue[];
  /** Shops on this plan now (paid or trial; the default plan counts everyone else). */
  subscribers: number;
  /** Monthly recurring revenue from those shops, in paisa (yearly counted as /12). */
  mrr_paisa: number;
  updated_at: string | null;
}

/** A pricing check the console shows while the owner edits (smart warnings). */
export interface PricingCheck {
  kind:
    | "no_price"
    | "yearly_not_cheaper"
    | "yearly_discount_big"
    | "cheaper_has_more"
    | "lower_limit_than_cheaper"
    | "same_as_other"
    | "losing_shops";
  plan_id: string;
  params: Record<string, string | number>;
}

export interface PricingBoard {
  plans: PricingPlan[];
  checks: PricingCheck[];
  totals: { paying_shops: number; mrr_paisa: number; shops: number };
}

/** Smart pricing checks: a cheaper plan with more, a yearly price that saves nothing, look-alikes. */
export function pricingChecks(plans: PricingPlan[]): PricingCheck[] {
  const out: PricingCheck[] = [];
  const monthlyOf = (p: PricingPlan) =>
    p.prices.monthly ?? (p.prices.yearly ? p.prices.yearly / 12 : 0);
  for (const p of plans) {
    if (p.is_default) continue;
    const m = p.prices.monthly;
    const y = p.prices.yearly;
    if (!m && !y) out.push({ kind: "no_price", plan_id: p.id, params: { plan: p.name } });
    if (m && y && y >= m * 12)
      out.push({ kind: "yearly_not_cheaper", plan_id: p.id, params: { plan: p.name } });
    if (m && y && y < m * 12 * 0.5)
      out.push({
        kind: "yearly_discount_big",
        plan_id: p.id,
        params: { plan: p.name, pct: Math.round((1 - y / (m * 12)) * 100) },
      });
  }
  // Compare each plan with every cheaper one (the default plan counts as the cheapest).
  const ranked = [...plans].sort((a, b) =>
    a.is_default ? -1 : b.is_default ? 1 : monthlyOf(a) - monthlyOf(b),
  );
  const value = (p: PricingPlan, key: string) => p.entitlements.find((e) => e.key === key)?.value;
  for (let i = 1; i < ranked.length; i++) {
    const p = ranked[i]!;
    if (!p.active && !p.is_default) continue;
    for (let j = 0; j < i; j++) {
      const cheaper = ranked[j]!;
      if (!cheaper.active && !cheaper.is_default) continue;
      if (!cheaper.is_default && monthlyOf(cheaper) === monthlyOf(p)) continue;
      const missing: string[] = [];
      const lower: string[] = [];
      for (const d of PLAN_FEATURES) {
        const a = value(cheaper, d.key);
        const b = value(p, d.key);
        if (a === undefined || b === undefined) continue;
        if (d.kind === "flag" && a === 1 && b === 0) missing.push(d.key);
        if (d.kind !== "flag" && b !== -1 && (a === -1 || a > b)) lower.push(d.key);
      }
      if (missing.length)
        out.push({
          kind: "cheaper_has_more",
          plan_id: p.id,
          params: {
            plan: p.name,
            cheaper: cheaper.name,
            keys: missing.join(","),
            n: missing.length,
          },
        });
      if (lower.length)
        out.push({
          kind: "lower_limit_than_cheaper",
          plan_id: p.id,
          params: { plan: p.name, cheaper: cheaper.name, keys: lower.join(","), n: lower.length },
        });
    }
  }
  // Two plans that give exactly the same.
  const sig = (p: PricingPlan) =>
    p.entitlements
      .map((e) => `${e.key}=${e.value}`)
      .sort()
      .join("|");
  for (let i = 0; i < plans.length; i++)
    for (let j = i + 1; j < plans.length; j++)
      if (sig(plans[i]!) === sig(plans[j]!))
        out.push({
          kind: "same_as_other",
          plan_id: plans[j]!.id,
          params: { plan: plans[j]!.name, other: plans[i]!.name },
        });
  return out;
}

// ---------- vendors (owner console) ----------

export type OrgLifecycleState = "trial" | "active" | "past_due" | "suspended" | "closed";

export interface VendorPlanView {
  plan_id: string;
  name: string;
  icon: PlanIcon;
  kind: "base" | "trial" | "paid";
  state: string;
  period: string;
  ends_at: string | null;
  auto_renew: boolean;
  /** Given by Adyvance staff (no charge). */
  granted: boolean;
}

export interface VendorListRow {
  id: string;
  name: string;
  slug: string;
  lifecycle: OrgLifecycleState;
  owner_email: string | null;
  plan: VendorPlanView;
  wallet_paisa: number;
  stores: number;
  staff: number;
  kyc_state: string | null;
  open_tickets: number;
  last_active_at: string | null;
  created_at: string | null;
  /** Health flags the directory highlights (renewal soon, low wallet, waiting top-up…). */
  flags: VendorFlag[];
  /** D-067: the health score and how much of the setup is done. */
  health: ShopHealth;
  setup_percent: number;
}

export type VendorFlag =
  | "renewal_soon"
  | "renewal_short"
  | "grace"
  | "low_wallet"
  | "topup_waiting"
  | "kyc_waiting"
  | "ticket_open"
  | "inactive"
  | "access_requested"
  | "at_risk"
  | "new";

export interface VendorDirectory {
  rows: VendorListRow[];
  counts: Record<OrgLifecycleState, number>;
  by_plan: { plan_id: string; name: string; icon: PlanIcon; shops: number }[];
}

export interface VendorUsageRow {
  key: string;
  kind: "flag" | "quota";
  /** Plan value (flags 0/1; quotas -1 = unlimited). */
  plan_value: number;
  /** After this shop's own overrides. */
  value: number;
  used: number | null;
  override: { value: number; expires_at: string | null; note: string | null } | null;
}

export interface SupportGrantView {
  id: string;
  state: "requested" | "approved" | "denied" | "revoked" | "expired" | "ended";
  reason: string;
  minutes: number;
  staff_name: string;
  requested_at: string | null;
  decided_at: string | null;
  expires_at: string | null;
  views: number;
}

export interface PlatformNoticeView {
  id: string;
  audience: "all" | "plan" | "org";
  plan_id: string | null;
  org_id: string | null;
  org_name: string | null;
  tone: "info" | "success" | "warning" | "critical";
  title: string;
  body: string;
  link: string | null;
  pinned_until: string | null;
  email: boolean;
  created_by: string;
  created_at: string | null;
  withdrawn_at: string | null;
  /** Shops that have seen it. */
  seen: number;
}

export interface VendorDetail {
  vendor: VendorListRow & { lifecycle_reason: string | null; lifecycle_at: string | null };
  owner: { name: string | null; email: string | null; phone: string | null } | null;
  stores: { id: string; name: string; slug: string; status: string; host: string | null }[];
  usage: VendorUsageRow[];
  subscriptions: {
    id: string;
    plan_name: string;
    kind: string;
    state: string;
    period: string;
    starts_at: string | null;
    ends_at: string | null;
    price_paisa: number | null;
    granted: boolean;
  }[];
  wallet: {
    balance_paisa: number;
    spent_30d_paisa: number;
    topups_waiting: number;
    recent: WalletActivity[];
  };
  services: {
    sms_credits: number;
    ai_credits: number;
    ads_running: number;
    import_orders_open: number;
    premium_themes: number;
  };
  activity: { action: string; actor: string; at: string | null }[];
  tickets: { id: string; subject: string; state: string; updated_at: string | null }[];
  access: SupportGrantView[];
  notices: PlatformNoticeView[];
  setup: { done: SetupStep[]; percent: number };
  /** D-067 batch 4: Adyvance's take-down of the public storefront (null when it is open). */
  storefront_pause: { at: string | null; reason: string | null } | null;
}

// ---------- console overview ----------

export interface ConsoleOverview {
  shops: {
    total: number;
    by_lifecycle: Record<OrgLifecycleState, number>;
    new_7d: number;
    new_30d: number;
    active_7d: number;
    paying: number;
  };
  money: {
    mrr_paisa: number;
    /** Wallet spending this month (what Adyvance earned from shops). */
    earned_month_paisa: number;
    earned_last_month_paisa: number;
    held_paisa: number;
    topups_waiting: number;
    topups_waiting_paisa: number;
  };
  plans: { plan_id: string; name: string; icon: PlanIcon; shops: number; mrr_paisa: number }[];
  renewals: { next_7d: number; auto_renew_off: number; short: number; in_grace: number };
  /** Things waiting for staff, each with a count and the console path that handles it. */
  attention: { kind: string; count: number; path: string }[];
  signups: { day: string; n: number }[];
  earned: { month: string; paisa: number }[];
  recent: { action: string; org_name: string | null; actor: string; at: string | null }[];
  /** D-067: shops by health band, and the lowest scores. */
  health: {
    healthy: number;
    watch: number;
    at_risk: number;
    lowest: { org_id: string; name: string; score: number; reasons: string[] }[];
  };
}

// ---------- vendor admin ----------

/** GET /orgs/{orgId}/notices: Adyvance messages for this shop. */
export interface ShopNotice {
  id: string;
  tone: PlatformNoticeView["tone"];
  title: string;
  body: string;
  link: string | null;
  pinned: boolean;
  seen: boolean;
  created_at: string | null;
}

/** GET /orgs/{orgId}/support-access: Adyvance's requests to view this shop. */
export interface ShopSupportGrant {
  id: string;
  state: SupportGrantView["state"];
  reason: string;
  minutes: number;
  staff_name: string;
  requested_at: string | null;
  expires_at: string | null;
  views: number;
}
